import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import CodeChip from '../coding/CodeChip';
import ReasonCodePicker from '../coding/ReasonCodePicker';
import { giveReasons } from '../../services/adjudicationService';
import { Panel, lockedLabel } from './parts';

const DIMS = ['ut', 'rc'];
const NAME = { ut: 'UT', rc: 'RC' };
const DIFFERS = { color: '#8A5A00', background: '#FFF6E0' };

const joinCodes = (codes) => (codes.length === 2 ? `${codes[0]} and ${codes[1]}` : codes.join(', '));

// After lock (wf-adj-item-reveal 1–5): the locked code read-only beside the earlier human label(s),
// without coder names, and one reason for each dimension that differs. Never model or LLM output,
// the flag, or why the item was selected.
export const RevealCompare = ({ view }) => {
    const { yours, earlier } = view;
    const two = earlier.length > 1;
    return (
        <Panel title="Your code is locked" aside={`Locked ${lockedLabel(yours.lockedAt)}`}>
            <Box role="table" aria-label="Your code and the earlier label" sx={{ display: 'grid', gap: 0.75 }}>
                <Box role="row" sx={{ display: 'grid', gridTemplateColumns: '48px 1fr 1.4fr 64px', gap: 1, px: 1 }}>
                    <span role="columnheader" />
                    <Typography role="columnheader" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Yours</Typography>
                    <Typography role="columnheader" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {two ? `Earlier labels (${earlier.length} annotators)` : 'Earlier label'}
                    </Typography>
                    <span role="columnheader" />
                </Box>
                {DIMS.map((dim) => {
                    const differs = view.reasonsNeeded[dim];
                    return (
                        <Box
                            key={dim}
                            role="row"
                            sx={{ display: 'grid', gridTemplateColumns: '48px 1fr 1.4fr 64px', gap: 1, alignItems: 'center', px: 1, py: 0.75, borderRadius: 1.5, bgcolor: differs ? DIFFERS.background : 'transparent' }}
                        >
                            <Typography role="rowheader" variant="body2" sx={{ fontWeight: 700 }}>{NAME[dim]}</Typography>
                            <Box role="cell"><CodeChip code={yours[dim]} coded /></Box>
                            <Box role="cell" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {earlier.map((e, i) => <CodeChip key={i} code={e[dim]} coded />)}
                            </Box>
                            <Typography role="cell" variant="caption" sx={{ fontWeight: 600, color: differs ? DIFFERS.color : 'text.secondary', textAlign: 'right' }}>
                                {differs ? 'differs' : 'same'}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
                Unsure: {yours.unsure ? 'yes' : 'no'}
            </Typography>
        </Panel>
    );
};

const RevealStep = ({ runId, view, onDone, onMovedOn }) => {
    const [reasons, setReasons] = useState({});
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const needed = useMemo(() => DIMS.filter((d) => view.reasonsNeeded[d]), [view]);
    const missing = needed.filter((d) => !reasons[d]);
    const two = view.earlier.length > 1;

    useEffect(() => {
        setReasons({});
        setNote('');
    }, [view.item.id]);

    const finish = async () => {
        if (missing.length) return;
        setBusy(true);
        setError(null);
        try {
            await giveReasons(runId, view.item.id, { reasons, note: note.trim() || undefined });
            onDone();
        } catch (err) {
            setBusy(false);
            if (err.response?.status === 409) onMovedOn();
            else setError('Couldn’t save. Try again.');
        }
    };

    return (
        <Panel title={needed.length ? 'Why do the codes differ?' : 'No reason needed'}>
            {needed.length === 0 && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Your code matches the earlier label on UT and RC. No reason needed.
                </Typography>
            )}
            <Box sx={{ display: 'grid', gap: 2 }}>
                {DIMS.map((dim) => (view.reasonsNeeded[dim] ? (
                    <ReasonCodePicker
                        key={dim}
                        id={`why-${dim}`}
                        dimensionLine={`${NAME[dim]} · yours ${view.yours[dim]}, earlier ${joinCodes(view.earlier.map((e) => e[dim]))}`}
                        reasons={view.reasonCodes[dim] || []}
                        value={reasons[dim] || null}
                        onChange={(code) => setReasons((r) => ({ ...r, [dim]: code }))}
                        disabled={busy}
                    />
                ) : needed.length > 0 && (
                    <Typography key={dim} variant="body2" color="text.secondary">
                        {NAME[dim]}: {two ? 'same as both' : 'same code'}, no reason needed
                    </Typography>
                )))}
            </Box>

            <TextField
                label="Note (optional)"
                placeholder="Anything the next reviewer should know"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                inputProps={{ maxLength: 2000 }}
                sx={{ mt: 2.5 }}
            />
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={busy || missing.length > 0}
                onClick={finish}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ mt: 2.5, minHeight: 46 }}
            >
                {needed.length ? 'Done · next item' : 'Next item'}
            </Button>
            {missing.length > 0 && (
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.25 }}>
                    Choose a reason for {missing.map((d) => NAME[d]).join(' and ')} to continue
                </Typography>
            )}
        </Panel>
    );
};

export default RevealStep;
